/**
 * NiV Support Firebase Service
 * 
 * Admin functions for managing support tickets.
 * These functions bypass security rules for admin operations.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
  arrayUnion,
  addDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { SUPPORT_TICKET_STATUSES } from "./supportFirebase";

// ============================================================================
// ADMIN FUNCTIONS
// ============================================================================

/**
 * Get all support tickets (admin view)
 * @returns {Promise<Array>} Array of all tickets
 */
export const getAllSupportTickets = async () => {
  try {
    const ticketsRef = collection(db, "supportTickets");
    const q = query(ticketsRef, orderBy("updatedAt", "desc"));

    const snapshot = await getDocs(q);
    const tickets = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate?.() || new Date(),
      closedAt: doc.data().closedAt?.toDate?.() || null,
    }));

    return tickets;
  } catch (error) {
    console.error("[CK-NIV-SUPPORT] Error fetching all tickets:", error);
    throw error;
  }
};

/**
 * Add an admin message to a ticket
 * When admin sends a message, status changes to "waiting_customer" and notification is sent
 * @param {string} ticketId - Ticket ID
 * @param {string} content - Message content
 * @param {string} clinicId - Clinic ID (for notification)
 * @param {string} therapistId - Therapist ID (for notification)
 * @returns {Promise<Object>} Updated ticket
 */
export const addAdminMessage = async (ticketId, content, clinicId, therapistId) => {
  try {
    if (!ticketId || !content) {
      throw new Error("Missing required fields for admin message");
    }

    const ticketRef = doc(db, "supportTickets", ticketId);
    
    const newMessage = {
      id: `msg_${Date.now()}`,
      senderId: "niv",
      senderName: "ניב (מנהל)",
      senderType: "admin",
      content,
      createdAt: new Date().toISOString(),
    };

    await updateDoc(ticketRef, {
      messages: arrayUnion(newMessage),
      status: SUPPORT_TICKET_STATUSES.WAITING_CUSTOMER,
      updatedAt: serverTimestamp(),
    });

    // Create notification for therapist
    if (clinicId && therapistId) {
      await createSupportNotification(
        clinicId,
        therapistId,
        "תגובה חדשה מצוות התמיכה",
        "קיבלת תגובה חדשה בפנייה שפתחת במרכז התמיכה",
        ticketId
      );
    }

    // Return updated ticket
    const ticketSnap = await getDoc(ticketRef);
    const data = ticketSnap.data();
    return {
      id: ticketSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate?.() || new Date(),
      updatedAt: data.updatedAt?.toDate?.() || new Date(),
      closedAt: data.closedAt?.toDate?.() || null,
    };
  } catch (error) {
    console.error("[CK-NIV-SUPPORT] Error adding admin message:", error);
    throw error;
  }
};

/**
 * Update ticket status (admin only)
 * @param {string} ticketId - Ticket ID
 * @param {string} status - New status
 * @param {string} clinicId - Clinic ID (for notification)
 * @param {string} therapistId - Therapist ID (for notification)
 * @param {boolean} shouldNotify - Whether to send notification
 * @returns {Promise<Object>} Updated ticket
 */
export const updateTicketStatus = async (ticketId, status, clinicId, therapistId, shouldNotify = false) => {
  try {
    if (!ticketId || !status) {
      throw new Error("Missing required fields for status update");
    }

    const ticketRef = doc(db, "supportTickets", ticketId);
    
    const updateData = {
      status,
      updatedAt: serverTimestamp(),
    };

    // If completing the ticket, set closedAt
    if (status === SUPPORT_TICKET_STATUSES.COMPLETED) {
      updateData.closedAt = serverTimestamp();
    }

    await updateDoc(ticketRef, updateData);

    // Send notification if completing or explicitly requested
    if (shouldNotify && clinicId && therapistId) {
      const isCompleted = status === SUPPORT_TICKET_STATUSES.COMPLETED;
      await createSupportNotification(
        clinicId,
        therapistId,
        isCompleted ? "הפנייה שלך הסתיימה" : "עדכון סטטוס פנייה",
        isCompleted 
          ? "הפנייה שלך במרכז התמיכה טופלה והסתיימה בהצלחה"
          : "הסטטוס של הפנייה שלך במרכז התמיכה עודכן",
        ticketId
      );
    }

    // Return updated ticket
    const ticketSnap = await getDoc(ticketRef);
    const data = ticketSnap.data();
    return {
      id: ticketSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate?.() || new Date(),
      updatedAt: data.updatedAt?.toDate?.() || new Date(),
      closedAt: data.closedAt?.toDate?.() || null,
    };
  } catch (error) {
    console.error("[CK-NIV-SUPPORT] Error updating ticket status:", error);
    throw error;
  }
};

/**
 * Delete a support ticket (admin only)
 * @param {string} ticketId - Ticket ID
 * @returns {Promise<void>}
 */
export const deleteSupportTicket = async (ticketId) => {
  try {
    if (!ticketId) {
      throw new Error("Missing ticketId");
    }

    const ticketRef = doc(db, "supportTickets", ticketId);
    await deleteDoc(ticketRef);

  } catch (error) {
    console.error("[CK-NIV-SUPPORT] Error deleting ticket:", error);
    throw error;
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Create a notification for a therapist about their support ticket
 * @param {string} clinicId - Clinic ID
 * @param {string} therapistId - Therapist ID
 * @param {string} title - Notification title
 * @param {string} message - Notification message
 * @param {string} ticketId - Ticket ID for link
 */
const createSupportNotification = async (clinicId, therapistId, title, message, ticketId) => {
  try {
    const notificationsRef = collection(db, "clinics", clinicId, "notifications");
    
    await addDoc(notificationsRef, {
      clinicId,
      therapistId,
      title,
      message,
      type: "support",
      urgency: 2, // Medium urgency (yellow)
      link: `/dashboard/support?ticket=${ticketId}`,
      readBy: [],
      createdBy: "system",
      timestamp: new Date().toISOString(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

  } catch (error) {
    console.error("[CK-NIV-SUPPORT] Error creating notification:", error);
    // Don't throw - notification failure shouldn't break the main operation
  }
};

/**
 * Get support ticket stats for Niv dashboard
 * @returns {Promise<Object>} Stats object
 */
export const getSupportTicketStats = async () => {
  try {
    const tickets = await getAllSupportTickets();
    
    return {
      total: tickets.length,
      open: tickets.filter((t) => t.status === SUPPORT_TICKET_STATUSES.OPEN).length,
      waitingAdmin: tickets.filter((t) => t.status === SUPPORT_TICKET_STATUSES.WAITING_ADMIN).length,
      waitingCustomer: tickets.filter((t) => t.status === SUPPORT_TICKET_STATUSES.WAITING_CUSTOMER).length,
      inProgress: tickets.filter((t) => t.status === SUPPORT_TICKET_STATUSES.IN_PROGRESS).length,
      stuck: tickets.filter((t) => t.status === SUPPORT_TICKET_STATUSES.STUCK).length,
      completed: tickets.filter((t) => t.status === SUPPORT_TICKET_STATUSES.COMPLETED).length,
    };
  } catch (error) {
    console.error("[CK-NIV-SUPPORT] Error getting support stats:", error);
    throw error;
  }
};
